// src/ui/FollowersPane.jsx — 👥 who is following a page, and a way into each.
//
// 🟢 THE SAME LIST FOR EVERY PAGE. Allo's own account is `pages/allo_official`,
//    so the follower count under Allo and the one under somebody's coffee shop
//    are the same field opening the same list. Nothing here knows which page it
//    is looking at.
//
// 🟥 THE PRIVACY FILTER IS IN services/pageFollow.js, NOT HERE, and it is the
//    phone's rule case for case. Two clients disagreeing about it does not look
//    like a bug — it looks like the web exposing somebody who chose to be
//    hidden on their phone.
//
// 🟥 AND THE HEADING COUNT IS THE PAGE'S, NOT THE LIST'S LENGTH. They are
//    allowed to differ: a hidden follower is counted and not listed. Showing
//    `rows.length` would silently redefine the number the moment one person
//    opted out, and the first report of it would be somebody saying their
//    follow "did not count".
import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft } from './icons';
import { loadFollowers, followerCountOf, FOLLOWER_PAGE } from '../services/pageFollow';
import Pic from './VibeAvatar';

export default function FollowersPane({ page, myUid, onBack, onOpenUser }) {
  const [rows, setRows] = useState([]);
  const [next, setNext] = useState(0);
  const [loading, setLoading] = useState(false);

  const total = followerCountOf(page);

  const fetchPage = useCallback(async (offset, replace) => {
    if (!page) return;
    setLoading(true);
    try {
      const r = await loadFollowers(page, myUid, offset, FOLLOWER_PAGE);
      setRows((prev) => (replace ? r.rows : [...prev, ...r.rows]));
      setNext(r.nextOffset);
    } catch {
      setNext(null);
    }
    setLoading(false);
  }, [page, myUid]);

  /* 🟥 RE-READ ON EVERY OPEN. A follower list is opened precisely to see who
     has arrived since last time; keeping the first answer would make it the one
     panel in the product that is reliably out of date. */
  useEffect(() => {
    setRows([]); setNext(0);
    fetchPage(0, true);
  }, [fetchPage]);

  /* some followers are counted and not shown — say so rather than leaving it to
     look like the list stopped loading */
  const hiddenSome = next === null && rows.length < total;

  return (
    <div className="vb-pane vb-followers">
      <div className="vb-pane-h">
        <button className="vb-pane-x" onClick={onBack} aria-label="Back">
          <ChevronLeft width={18} height={18} />
        </button>
        <b>Followers</b>
        <span className="vb-sub">{total}</span>
      </div>

      <div className="vb-foll-list">
        {rows.map((u) => (
          <button
            key={u.uid}
            type="button"
            className="vb-foll-row"
            /* 🟢 THE SAME OPENER THE FEED USES, given the same object shape — so
               a follower opens the profile a tapped byline would have opened,
               with no second route into a person. */
            onClick={() => onOpenUser?.({ uid: u.uid, name: u.fullName || u.username, image: u.profileImage })}
          >
            <Pic src={u.profileImage} name={u.fullName || u.username} size={42} />
            <span className="vb-foll-who">
              <b>{u.fullName || u.username || 'Allo user'}</b>
              {u.username ? <i>@{u.username}</i> : null}
            </span>
          </button>
        ))}

        {!rows.length && !loading && <div className="ct-empty"><span>No followers yet.</span></div>}
        {loading && <div className="vb-sub vb-foll-note">Loading…</div>}
        {next !== null && !loading && rows.length > 0 && (
          <button className="btn ghost vb-foll-more" onClick={() => fetchPage(next, false)}>Show more</button>
        )}
        {hiddenSome && (
          <div className="vb-sub vb-foll-note">Some followers choose not to appear here.</div>
        )}
      </div>
    </div>
  );
}
